import { useState } from "react";

type AuthMode = "login" | "signup";
type ContactMethod = "email" | "phone";

interface Props {
  onClose: () => void;
  onSuccess: (name: string) => void;
}

export default function BuyerAuthModal({ onClose, onSuccess }: Props) {
  const [mode, setMode] = useState<AuthMode>("signup");
  const [method, setMethod] = useState<ContactMethod>("email");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [password, setPassword] = useState("");
  const [fullName, setFullName] = useState("");
  const [company, setCompany] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    const contact = method === "email" ? email : phone;
    if (!contact || !password) {
      setError("Please fill in all required fields.");
      return;
    }
    if (mode === "signup" && !fullName) {
      setError("Please enter your full name.");
      return;
    }
    setLoading(true);
    setTimeout(() => {
      setLoading(false);
      onSuccess(mode === "signup" ? fullName : "Buyer");
    }, 1200);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/50" onClick={onClose}></div>
      <div className="relative bg-white rounded-2xl w-full max-w-md overflow-hidden">
        {/* Header */}
        <div className="bg-gradient-to-r from-emerald-500 to-teal-500 px-6 py-5">
          <button onClick={onClose} className="absolute top-4 right-4 w-8 h-8 flex items-center justify-center text-white/80 hover:text-white cursor-pointer">
            <i className="ri-close-line text-xl"></i>
          </button>
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 flex items-center justify-center bg-white/20 rounded-xl">
              <i className="ri-user-star-line text-white text-xl"></i>
            </div>
            <div>
              <h2 className="text-white font-extrabold text-lg">
                {mode === "login" ? "Buyer Login" : "Join as Buyer — FREE"}
              </h2>
              <p className="text-white/80 text-xs">Post requirements, get quotes from factories worldwide</p>
            </div>
          </div>
        </div>

        <div className="p-6">
          {/* Free badge */}
          {mode === "signup" && (
            <div className="flex items-center gap-2 bg-emerald-50 border border-emerald-200 rounded-xl px-4 py-3 mb-4">
              <i className="ri-gift-line text-emerald-500 text-lg"></i>
              <div>
                <p className="text-emerald-700 font-bold text-sm">Buyer Account is 100% FREE</p>
                <p className="text-emerald-600 text-xs">Post unlimited requirements, receive quotes from verified factories</p>
              </div>
            </div>
          )}

          {/* Mode toggle */}
          <div className="flex bg-gray-100 rounded-full p-1 mb-5">
            <button
              onClick={() => setMode("signup")}
              className={`flex-1 py-2 rounded-full text-sm font-semibold transition-all cursor-pointer whitespace-nowrap ${mode === "signup" ? "bg-white text-gray-900" : "text-gray-500"}`}
            >
              Sign Up Free
            </button>
            <button
              onClick={() => setMode("login")}
              className={`flex-1 py-2 rounded-full text-sm font-semibold transition-all cursor-pointer whitespace-nowrap ${mode === "login" ? "bg-white text-gray-900" : "text-gray-500"}`}
            >
              Login
            </button>
          </div>

          {/* Contact method toggle */}
          <div className="flex gap-2 mb-4">
            <button
              onClick={() => setMethod("email")}
              className={`flex-1 flex items-center justify-center gap-2 py-2.5 rounded-xl text-sm font-semibold border transition-all cursor-pointer whitespace-nowrap ${method === "email" ? "border-emerald-400 bg-emerald-50 text-emerald-600" : "border-gray-200 text-gray-500 hover:border-emerald-200"}`}
            >
              <i className="ri-mail-line"></i> Email
            </button>
            <button
              onClick={() => setMethod("phone")}
              className={`flex-1 flex items-center justify-center gap-2 py-2.5 rounded-xl text-sm font-semibold border transition-all cursor-pointer whitespace-nowrap ${method === "phone" ? "border-emerald-400 bg-emerald-50 text-emerald-600" : "border-gray-200 text-gray-500 hover:border-emerald-200"}`}
            >
              <i className="ri-smartphone-line"></i> Phone
            </button>
          </div>

          <form onSubmit={handleSubmit} className="flex flex-col gap-3">
            {mode === "signup" && (
              <>
                <div>
                  <label className="text-xs font-semibold text-gray-600 mb-1 block">Full Name *</label>
                  <input
                    type="text"
                    value={fullName}
                    onChange={e => setFullName(e.target.value)}
                    placeholder="Your full name"
                    className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-emerald-400 transition-colors"
                  />
                </div>
                <div>
                  <label className="text-xs font-semibold text-gray-600 mb-1 block">Company / Business (optional)</label>
                  <input
                    type="text"
                    value={company}
                    onChange={e => setCompany(e.target.value)}
                    placeholder="Your company name"
                    className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-emerald-400 transition-colors"
                  />
                </div>
              </>
            )}

            {method === "email" ? (
              <div>
                <label className="text-xs font-semibold text-gray-600 mb-1 block">Email Address *</label>
                <input
                  type="email"
                  name="email"
                  value={email}
                  onChange={e => setEmail(e.target.value)}
                  placeholder="buyer@example.com"
                  className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-emerald-400 transition-colors"
                />
              </div>
            ) : (
              <div>
                <label className="text-xs font-semibold text-gray-600 mb-1 block">Phone Number *</label>
                <div className="flex gap-2">
                  <select className="border border-gray-200 rounded-xl px-3 py-3 text-sm focus:outline-none focus:border-emerald-400 bg-white cursor-pointer">
                    <option>+1</option>
                    <option>+44</option>
                    <option>+49</option>
                    <option>+33</option>
                    <option>+61</option>
                    <option>+81</option>
                    <option>+82</option>
                    <option>+91</option>
                    <option>+86</option>
                    <option>+55</option>
                    <option>+971</option>
                    <option>+234</option>
                  </select>
                  <input
                    type="tel"
                    value={phone}
                    onChange={e => setPhone(e.target.value)}
                    placeholder="Phone number"
                    className="flex-1 border border-gray-200 rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-emerald-400 transition-colors"
                  />
                </div>
              </div>
            )}

            <div>
              <label className="text-xs font-semibold text-gray-600 mb-1 block">Password *</label>
              <input
                type="password"
                value={password}
                onChange={e => setPassword(e.target.value)}
                placeholder="••••••••"
                className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-emerald-400 transition-colors"
              />
            </div>

            {error && (
              <div className="bg-red-50 border border-red-200 text-red-600 text-xs rounded-xl px-4 py-3">
                <i className="ri-error-warning-line mr-1.5"></i>{error}
              </div>
            )}

            <button
              type="submit"
              disabled={loading}
              className="w-full bg-emerald-500 hover:bg-emerald-600 disabled:opacity-60 text-white font-bold py-3.5 rounded-full transition-colors cursor-pointer whitespace-nowrap text-sm mt-1"
            >
              {loading ? (
                <span className="flex items-center justify-center gap-2">
                  <i className="ri-loader-4-line animate-spin"></i> Processing...
                </span>
              ) : mode === "signup" ? "Create Buyer Account — FREE" : "Login to My Account"}
            </button>
          </form>

          <p className="text-center text-xs text-gray-400 mt-4 leading-relaxed">
            Buyer accounts are always free. Post requirements and receive quotes from 20 Million+ verified factories worldwide.
          </p>
        </div>
      </div>
    </div>
  );
}
